import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPlaceholderUrl } from '../../../../utils/PlaceholderCreator';

const RANK_THRESHOLDS = [
    { limit: 300, rank: 'G', next: 300 },
    { limit: 600, rank: 'G+', next: 600 },
    { limit: 900, rank: 'F', next: 900 },
    { limit: 1300, rank: 'F+', next: 1300 },
    { limit: 1800, rank: 'E', next: 1800 },
    { limit: 2300, rank: 'E+', next: 2300 },
    { limit: 2900, rank: 'D', next: 2900 },
    { limit: 3500, rank: 'D+', next: 3500 },
    { limit: 4900, rank: 'C', next: 4900 },
    { limit: 6500, rank: 'C+', next: 6500 },
    { limit: 8200, rank: 'B', next: 8200 },
    { limit: 10000, rank: 'B+', next: 10000 },
    { limit: 12100, rank: 'A', next: 12100 },
    { limit: 14500, rank: 'A+', next: 14500 },
    { limit: 15900, rank: 'S', next: 15900 },
    { limit: 17500, rank: 'S+', next: 17500 },
    { limit: 19200, rank: 'SS', next: 19200 },
    { limit: 19600, rank: 'SS+', next: 19600 },
    { limit: 20000, rank: 'UG', next: 20000 },
    { limit: 20400, rank: 'UG1', next: 20400 },
    { limit: 20800, rank: 'UG2', next: 20800 },
    { limit: 21200, rank: 'UG3', next: 21200 },
    { limit: 21600, rank: 'UG4', next: 21600 },
    { limit: 22100, rank: 'UG5', next: 22100 },
    { limit: Infinity, rank: 'UG6', next: null }
];

const TotalScoreRanking = ({ totalScore }) => {
    const { t } = useTranslation();
    // Determine Rank
    let currentRank = 'G';
    let nextRankScore = 300;
    let pointsToNext = 300;

    for (let i = 0; i < RANK_THRESHOLDS.length; i++) {
        const threshold = RANK_THRESHOLDS[i];
        if (totalScore < threshold.limit) {
            currentRank = threshold.rank;
            nextRankScore = threshold.limit;
            pointsToNext = nextRankScore - totalScore;
            break;
        }
    }

    // Handle UG6 case (Infinity)
    if (totalScore >= 22100) {
        currentRank = 'UG6';
        pointsToNext = 0;
    }

    // Image URL logic
    // Normal ranks: {rank}.png
    // Plus ranks: {rank}_1.png (e.g. G+ -> G_1.png)
    // UG ranks: UG1.png, UG2.png...
    // Special handling for the filename format based on user request
    const getRankImage = (rank) => {
        let filename = rank;
        if (rank.includes('+')) {
            filename = `${rank.replace('+', '')}_1`;
        }
        // UG ranks are already like UG1, UG2... except UG itself which is UG.png
        // But user said: UG, UG1, UG2...
        // Let's check the list provided in AllScoreSume.txt:
        // UG -> UG.png
        // UG1 -> UG1.png
        // UG2 -> UG2.png
        // ...
        // UG6 -> UG6.png

        return `/images/Letras/${filename}.png`;
    };

    // Styling logic copied and adapted from AptitudePanel.js
    const getBadgeStyle = (rank) => {
        // Extract base letter (e.g., "A+" -> "A", "UG1" -> "UG")
        let base = rank.replace('+', '').replace(/[0-9]/g, '');
        // Special case for SS -> S style (or maybe special SS style? Keeping S logic for now as requested "same background")
        // But SS usually implies S color logic or better. User asked for "S, A, B..." logic.
        // Let's map SS to S color for consistency with "Yellow".
        if (base === 'SS') base = 'S';

        switch (base) {
            case 'S': return 'bg-gradient-to-br from-yellow-50 to-yellow-100 ring-yellow-400/50';
            case 'A': return 'bg-gradient-to-br from-rose-50 to-rose-100 ring-rose-400/50';
            case 'B': return 'bg-gradient-to-br from-pink-50 to-pink-100 ring-pink-400/50';
            case 'C': return 'bg-gradient-to-br from-emerald-50 to-emerald-100 ring-emerald-400/50';
            case 'D': return 'bg-gradient-to-br from-sky-50 to-sky-100 ring-sky-400/50';
            case 'E': return 'bg-gradient-to-br from-violet-50 to-violet-100 ring-violet-400/50';
            case 'F': return 'bg-gradient-to-br from-slate-100 to-slate-200 ring-slate-400/50';
            case 'G': return 'bg-gradient-to-br from-gray-100 to-gray-200 ring-gray-400/50';
            case 'UG': return 'bg-gradient-to-br from-indigo-50 to-purple-100 ring-indigo-400/50'; // Purple/Violet/Blue pastel mix
            default: return 'bg-gray-50 ring-gray-200';
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center justify-between relative overflow-hidden border-2 border-yellow-400/30">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-100 rounded-full blur-3xl -mr-16 -mt-16 opacity-50"></div>

            <div className="z-10 flex flex-col">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">{t('TotalScoreRanking.label_total_score')}</h3>
                <div className="text-4xl font-black text-gray-800 tracking-tight">
                    {totalScore.toLocaleString()}
                </div>
                {pointsToNext > 0 ? (
                    <div className="text-xs text-gray-500 mt-2 font-medium">
                        {t('TotalScoreRanking.label_next_rank')} <span className="text-blue-600">+{pointsToNext}</span>
                    </div>
                ) : (
                    <div className="text-xs text-yellow-600 mt-2 font-bold">
                        {t('TotalScoreRanking.msg_max_rank')}
                    </div>
                )}
            </div>

            <div className={`z-10 flex items-center justify-center w-24 h-24 rounded-full shadow-sm ring-4 ring-offset-2 transition-colors duration-200 ${getBadgeStyle(currentRank)}`}>
                <img
                    src={getRankImage(currentRank)}
                    alt={`Rank ${currentRank}`}
                    className="w-16 h-16 object-contain drop-shadow-sm filter brightness-105"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = createPlaceholderUrl(64, 64, '?');
                    }}
                />
            </div>
        </div>
    );
};

export default TotalScoreRanking;
// Revisado: 2025-12-18 [I18n Scan]
